import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";

import type { CreateMessageFieldProps } from "./CreateMessageField";
import type { TextComposerDerivedOutput } from "../channel/channelDerivation";
import {
  selectActivePreviewChannel,
  selectDerivedOutput,
  selectDisplayValues,
  selectFrameOutput,
  selectIsAuthoringValid,
  selectIsCtaValid,
  selectIsSuggestionsValid,
  selectNormalizedAuthoringState,
  selectSelectedProductModel,
} from "../editor/messageDraftSelectors";
import type { MessageDraftState } from "../editor/messageDraftStore";
import { useMessageDraftStore } from "../editor/messageDraftStoreFactory";
import { isTextComposerRcsRichEnabledForSite } from "../flag";
import { extractPreviewBodyText } from "../preview/previewContentUtils";
import type { TextComposerPreviewContent } from "../preview/useMessagePreview";

import { useDynamicFlag } from "@/utils/featureFlagOverride";

/** Server-rendered preview content keyed by transport channel. */
export type PreviewByChannel = Partial<
  Record<"SMS" | "MMS" | "RCS", TextComposerPreviewContent | null>
>;

export function useMessageDraftSelectors(options?: {
  previewByChannel?: PreviewByChannel;
  channelPair?: TextComposerDerivedOutput;
}) {
  const previewByChannel = options?.previewByChannel;
  const channelPairOverride = options?.channelPair;
  const authoringState = useMessageDraftStore((s) => s.authoringState);
  const previewRecipientViewMode = useMessageDraftStore(
    (s) => s.previewRecipientViewMode,
  );
  const dragonTileCategory = useMessageDraftStore((s) => s.dragonTileCategory);
  const actions = useMessageDraftStore((s) => s.actions);

  const cursorPosition = useMessageDraftStore((s) => s.cursorPosition);

  const { siteId } = useParams({ strict: false });
  const rcsRichEnabled = useDynamicFlag(
    "VITE_FF_TEXT_COMPOSER_RCS_RICH",
    isTextComposerRcsRichEnabledForSite(siteId),
  );

  const snapshot: MessageDraftState = useMemo(
    () => ({
      authoringState,
      cursorPosition,
      previewRecipientViewMode,
      dragonTileCategory,
    }),
    [
      authoringState,
      cursorPosition,
      previewRecipientViewMode,
      dragonTileCategory,
    ],
  );

  const normalizedAuthoringState = useMemo(
    () => selectNormalizedAuthoringState(snapshot),
    [snapshot],
  );
  const derivedOutput = useMemo(
    () => channelPairOverride ?? selectDerivedOutput(snapshot),
    [channelPairOverride, snapshot],
  );
  const previewBodyText = useMemo(() => {
    if (!previewByChannel) return undefined;
    const activeChannel = selectActivePreviewChannel(snapshot);
    if (
      activeChannel !== "SMS" &&
      activeChannel !== "MMS" &&
      activeChannel !== "RCS"
    ) {
      return undefined;
    }
    return extractPreviewBodyText(previewByChannel[activeChannel]);
  }, [previewByChannel, snapshot]);
  const displayValues = useMemo(
    () => selectDisplayValues(snapshot, previewBodyText, rcsRichEnabled),
    [snapshot, previewBodyText, rcsRichEnabled],
  );
  const frameOutput = useMemo(() => selectFrameOutput(snapshot), [snapshot]);
  const isAuthoringValid = useMemo(
    () => selectIsAuthoringValid(snapshot),
    [snapshot],
  );
  const isCtaValid = useMemo(() => selectIsCtaValid(snapshot), [snapshot]);
  const isSuggestionsValid = useMemo(
    () => selectIsSuggestionsValid(snapshot),
    [snapshot],
  );
  const selectedProductModel = useMemo(
    () => selectSelectedProductModel(snapshot),
    [snapshot],
  );

  const messageDraftSelectors: Pick<
    CreateMessageFieldProps,
    | "authoringState"
    | "onBodyTextChange"
    | "primaryChannel"
    | "fallbackChannel"
    | "previewRecipientViewMode"
    | "onPreviewRecipientViewModeChange"
    | "transportCountLabel"
    | "messageStatsTooltip"
    | "characterCount"
    | "isNearCharacterLimit"
    | "characterLimit"
    | "showCharacterCountApproximation"
  > = useMemo(
    () => ({
      authoringState: normalizedAuthoringState,
      onBodyTextChange: actions.setBodyText,
      primaryChannel: derivedOutput.primaryChannel,
      fallbackChannel: derivedOutput.fallbackChannel,
      previewRecipientViewMode,
      onPreviewRecipientViewModeChange: actions.setPreviewRecipientViewMode,
      transportCountLabel: displayValues.transportCountLabel,
      messageStatsTooltip: displayValues.messageStatsTooltip,
      characterCount: displayValues.displayedCharacterCount,
      isNearCharacterLimit: displayValues.isNearCharacterLimit,
      characterLimit: displayValues.displayedCharacterLimit,
      showCharacterCountApproximation:
        displayValues.showCharacterCountApproximation,
    }),
    [
      actions,
      derivedOutput.fallbackChannel,
      derivedOutput.primaryChannel,
      displayValues.displayedCharacterCount,
      displayValues.displayedCharacterLimit,
      displayValues.isNearCharacterLimit,
      displayValues.messageStatsTooltip,
      displayValues.transportCountLabel,
      displayValues.showCharacterCountApproximation,
      normalizedAuthoringState,
      previewRecipientViewMode,
    ],
  );

  return {
    authoringState: normalizedAuthoringState,
    derivedOutput,
    displayValues,
    frameOutput,
    isAuthoringValid,
    isCtaValid,
    isSuggestionsValid,
    selectedProductModel,
    previewRecipientViewMode,
    actions,
    messageDraftSelectors,
  };
}
